import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Radio, RadioGroup, FormControlLabel, Button, Alert,
  TextField, Switch, Divider, Chip,
} from '@mui/material';
// FormControlLabel is re-used as MuiFormControlLabel for the Switch rows
const MuiFormControlLabel = FormControlLabel;
import { getAnnotatorSettings, updateAnnotatorSettings } from '../../api/annotatorSettings';
import {
  getPipelineSettings, updatePipelineSetting, getPipelineStatus, triggerScanNow,
  type PipelineSetting, type PipelineStatus,
} from '../../api/annotatorPipeline';
import { useAuth } from '../../contexts/AuthContext';
import type { AppSettings } from '../../types/annotator';

const BACKENDS: { value: string; label: string; description: string }[] = [
  { value: 'libreyolo', label: 'LibreYOLO (YOLOX)', description: 'YOLOX-based backend for training and inference.' },
];

// Keys we render as named fields in the pipeline settings section
const PIPELINE_KEYS: { key: string; label: string; description: string; type: 'number' | 'text' | 'bool' }[] = [
  { key: 'pipeline_enabled', label: 'Pipeline Enabled', description: 'Enable or disable automatic FTP scan & inference.', type: 'bool' },
  { key: 'scan_interval_seconds', label: 'Scan Interval (seconds)', description: 'How often the scanner polls FTP folders for new images. Default: 300 (5 min).', type: 'number' },
  { key: 'ftps_base_path', label: 'FTP Base Path', description: 'Root folder on disk where device UUID subfolders live. e.g. /app/ftps/uploads', type: 'text' },
  { key: 'inference_url', label: 'Inference API URL', description: 'URL of the YOLO inference API used to auto-annotate new images. e.g. http://api-inference:8044', type: 'text' },
];

export default function AnnotatorSettingsPage() {
  const auth = useAuth();
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [pending, setPending] = useState<'libreyolo'>('libreyolo');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Pipeline settings state
  const [pipelineSettings, setPipelineSettings] = useState<Record<string, string>>({});
  const [pipelinePending, setPipelinePending] = useState<Record<string, string>>({});
  const [pipelineSaving, setPipelineSaving] = useState(false);
  const [pipelineSaved, setPipelineSaved] = useState(false);
  const [pipelineError, setPipelineError] = useState('');
  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus | null>(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    getAnnotatorSettings()
      .then((s) => { setSettings(s); setPending((s.training_backend as 'libreyolo') ?? 'libreyolo'); })
      .catch(() => setError('Failed to load settings.'));

    getPipelineSettings()
      .then((rows: PipelineSetting[]) => {
        const map: Record<string, string> = {};
        rows.forEach((r) => { map[r.key] = r.value; });
        // Apply defaults for keys not yet in DB
        if (!map['pipeline_enabled']) map['pipeline_enabled'] = 'true';
        if (!map['scan_interval_seconds']) map['scan_interval_seconds'] = '300';
        setPipelineSettings(map);
        setPipelinePending({ ...map });
      })
      .catch(() => setPipelineError('Failed to load pipeline settings.'));

    getPipelineStatus()
      .then(setPipelineStatus)
      .catch(() => { /* status is optional */ });
  }, []);

  async function handleSave() {
    if (!pending || pending === settings?.training_backend) return;
    setSaving(true); setSaved(false); setError('');
    try {
      const updated = await updateAnnotatorSettings({ training_backend: pending });
      setSettings(updated); setPending((updated.training_backend as 'libreyolo') ?? 'libreyolo');
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setSaving(false); }
  }

  async function handleSavePipeline() {
    setPipelineSaving(true); setPipelineSaved(false); setPipelineError('');
    try {
      for (const { key } of PIPELINE_KEYS) {
        if (pipelinePending[key] !== undefined && pipelinePending[key] !== pipelineSettings[key]) {
          await updatePipelineSetting(key, pipelinePending[key]);
        }
      }
      setPipelineSettings({ ...pipelinePending });
      setPipelineSaved(true); setTimeout(() => setPipelineSaved(false), 3000);
    } catch (err: unknown) {
      setPipelineError(err instanceof Error ? err.message : String(err));
    } finally { setPipelineSaving(false); }
  }

  async function handleScanNow() {
    setScanning(true);
    try {
      await triggerScanNow();
      // Refresh status after a short delay
      setTimeout(() => {
        getPipelineStatus().then(setPipelineStatus).catch(() => {});
      }, 2000);
    } catch (err: unknown) {
      setPipelineError(err instanceof Error ? err.message : String(err));
    } finally { setScanning(false); }
  }

  const isAdmin = auth.hasRole('DMO_ADMIN');
  const isDirty = pending !== (settings?.training_backend ?? 'libreyolo');
  const isPipelineDirty = PIPELINE_KEYS.some(({ key }) => pipelinePending[key] !== pipelineSettings[key]);

  if (!settings) return <Typography color="text.secondary" sx={{ p: 4 }}>{error || 'Loading…'}</Typography>;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>Settings</Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Training & Detection Backend</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Controls which framework is used for model training and detection. Applies globally.
          {!isAdmin && <strong> Contact an administrator to change this setting.</strong>}
        </Typography>

        <RadioGroup value={pending} onChange={(e) => isAdmin && setPending(e.target.value as 'libreyolo')}>
          {BACKENDS.map((b) => (
            <Paper
              key={b.value}
              variant="outlined"
              sx={{
                p: 2, mb: 1,
                borderColor: pending === b.value ? 'primary.main' : 'divider',
                opacity: isAdmin ? 1 : 0.7,
              }}
            >
              <FormControlLabel
                value={b.value}
                control={<Radio disabled={!isAdmin} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{b.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{b.description}</Typography>
                  </Box>
                }
              />
            </Paper>
          ))}
        </RadioGroup>

        {isAdmin && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving || !isDirty}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {saved && <Typography variant="body2" color="success.main">Saved</Typography>}
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

      {/* ── Annotator Pipeline Settings ───────────────────────────────────── */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Annotator Pipeline</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Controls the background FTP scan that picks up new device images, runs YOLO inference, and
          queues them as <em>unverified</em> annotations for human review before training.
          {!isAdmin && <strong> Admin-only.</strong>}
        </Typography>

        {pipelineStatus && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              size="small"
              label={pipelineSettings['pipeline_enabled'] === 'true' ? 'Enabled' : 'Disabled'}
              color={pipelineSettings['pipeline_enabled'] === 'true' ? 'success' : 'default'}
            />
            {pipelineStatus.last_scan_at && (
              <Typography variant="caption" color="text.secondary">
                Last scan: {new Date(pipelineStatus.last_scan_at).toLocaleString()} &nbsp;|&nbsp;
                {pipelineStatus.last_scan_imported} imported &nbsp;|&nbsp;
                {pipelineStatus.last_scan_errors} errors
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {PIPELINE_KEYS.map(({ key, label, description, type }) => {
            if (type === 'bool') {
              return (
                <Box key={key}>
                  <MuiFormControlLabel
                    control={
                      <Switch
                        checked={pipelinePending[key] === 'true'}
                        onChange={(e) => isAdmin && setPipelinePending((p) => ({ ...p, [key]: e.target.checked ? 'true' : 'false' }))}
                        disabled={!isAdmin}
                      />
                    }
                    label={<Typography variant="body2" fontWeight={500}>{label}</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>{description}</Typography>
                </Box>
              );
            }
            return (
              <TextField
                key={key}
                size="small"
                label={label}
                helperText={description}
                type={type === 'number' ? 'number' : 'text'}
                value={pipelinePending[key] ?? ''}
                onChange={(e) => isAdmin && setPipelinePending((p) => ({ ...p, [key]: e.target.value }))}
                disabled={!isAdmin}
                inputProps={type === 'number' ? { min: 10 } : undefined}
              />
            );
          })}
        </Box>

        {isAdmin && (
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={handleSavePipeline} disabled={pipelineSaving || !isPipelineDirty}>
              {pipelineSaving ? 'Saving…' : 'Save Pipeline Settings'}
            </Button>
            <Button variant="outlined" onClick={handleScanNow} disabled={scanning}>
              {scanning ? 'Triggering…' : 'Scan Now'}
            </Button>
            {pipelineSaved && <Typography variant="body2" color="success.main">Saved</Typography>}
          </Box>
        )}
        {pipelineError && <Alert severity="error" sx={{ mt: 2 }}>{pipelineError}</Alert>}
      </Paper>
    </Box>
  );
}
